import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ITrustedState } from '../sso-login.models';

@Component({
  selector: 'app-trusted-device',
  templateUrl: './trusted-device.component.html',
  styleUrls: ['./trusted-device.component.scss'],
  standalone: true,
  imports: [CommonModule],
})
export class TrustedDeviceComponent implements AfterViewInit, DoCheck {
  @Input({ required: true }) trusted!: ITrustedState;
  @Input() showTrustButton = true;

  @Output() trustRequested = new EventEmitter<void>();
  @Output() untrustedRequested = new EventEmitter<void>();
  @Output() deviceSelected = new EventEmitter<string>();
  @Output() removalConfirmed = new EventEmitter<void>();
  @Output() selectionCancelled = new EventEmitter<void>();
  @Output() codeVerified = new EventEmitter<void>();
  @Output() codeResent = new EventEmitter<void>();
  @Output() codeCancelled = new EventEmitter<void>();

  readonly cells = [0, 1, 2, 3, 4, 5];
  digits: string[] = ['', '', '', '', '', ''];
  shake = false;

  private prevStage: string | null = null;
  private prevError = '';

  @ViewChildren('codeCell') cellInputs!: QueryList<ElementRef<HTMLInputElement>>;

  get isCodeComplete(): boolean {
    return this.digits.every((d) => d !== '');
  }

  ngDoCheck(): void {
    if (!this.trusted) {
      return;
    }

    if ((this.trusted.code || '') !== this.digits.join('')) {
      const clean = (this.trusted.code || '').replace(/\D/g, '').slice(0, 6);
      this.digits = Array.from({ length: 6 }, (_, i) => clean[i] ?? '');
      this.syncInputs();
    }

    if (this.trusted.stage === 'code' && this.prevStage !== 'code') {
      setTimeout(() => this.focusCell(this.firstEmptyIndex()), 80);
    }
    this.prevStage = this.trusted.stage;

    if (
      this.trusted.stage === 'code' &&
      this.trusted.error &&
      this.trusted.error !== this.prevError
    ) {
      this.shake = true;
      setTimeout(() => (this.shake = false), 450);
    }
    this.prevError = this.trusted.error;
  }

  ngAfterViewInit(): void {
    if (this.trusted?.stage === 'code') {
      setTimeout(() => this.focusCell(this.firstEmptyIndex()), 80);
    }
  }

  onCodeInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const clean = input.value.replace(/\D/g, '');

    if (!clean) {
      this.digits[index] = '';
      this.syncInputs();
      this.writeCode();
      return;
    }

    let i = index;
    for (const ch of clean) {
      if (i > 5) break;
      this.digits[i] = ch;
      i++;
    }
    this.syncInputs();
    this.writeCode();

    if (this.isCodeComplete) {
      this.focusCell(5);
    } else {
      this.focusCell(this.firstEmptyIndex(Math.min(i, 5)));
    }
  }

  onCodeKeyDown(index: number, event: KeyboardEvent): void {
    const key = event.key;

    if (key === 'Backspace') {
      event.preventDefault();
      if (this.digits[index]) {
        this.digits[index] = '';
      } else if (index > 0) {
        this.digits[index - 1] = '';
        this.focusCell(index - 1);
      }
      this.syncInputs();
      this.writeCode();
      return;
    }

    if (key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusCell(index - 1, true);
      return;
    }

    if (key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.focusCell(index + 1, true);
      return;
    }

    if (key === 'Enter') {
      if (this.isCodeComplete && this.trusted.loadingAction === null) {
        this.codeVerified.emit();
      }
      return;
    }

    if (key.length === 1 && !/\d/.test(key) && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
    }
  }

  onCodePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clean = (event.clipboardData?.getData('text') || '')
      .replace(/\D/g, '')
      .slice(0, 6);
    if (!clean) {
      return;
    }
    this.digits = Array.from({ length: 6 }, (_, i) => clean[i] ?? '');
    this.syncInputs();
    this.writeCode();
    if (this.isCodeComplete) {
      this.focusCell(5);
    } else {
      this.focusCell(this.firstEmptyIndex());
    }
  }

  onCodeFocus(event: FocusEvent): void {
    (event.target as HTMLInputElement).select();
  }

  private writeCode(): void {
    this.trusted.code = this.digits.join('');
    if (this.trusted.error) {
      this.trusted.error = '';
    }
  }

  private syncInputs(): void {
    this.cellInputs?.forEach((ref, i) => {
      ref.nativeElement.value = this.digits[i];
    });
  }

  private focusCell(index: number, select = false): void {
    const el = this.cellInputs?.get(index)?.nativeElement;
    if (!el) {
      return;
    }
    el.focus();
    if (select) {
      el.select();
    }
  }

  private firstEmptyIndex(from = 0): number {
    for (let i = 0; i < 6; i++) {
      if (this.digits[i] === '') {
        return i;
      }
    }
    return Math.min(from, 5);
  }
}
